/**
 * Compact site navigation for public demo pages (Home / Back / hamburger).
 * Keeps citizens from getting stuck without a path back to InterviewFlow.
 */
import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const PS_BASE = 'https://interviewflow.studio/publicSector';

function cityQuery(searchParams) {
  const city = String(searchParams.get('city') || searchParams.get('jurisdiction') || '').trim();
  return city ? `?city=${encodeURIComponent(city)}` : '';
}

function buildNavSections(cityQs) {
  return [
    {
      id: 'demos',
      label: 'City Hall demos',
      items: [
        { label: 'Home', to: '/', exact: true },
        { label: 'Advisor', to: `/v2/demo/permit${cityQs}` },
        { label: 'Business', to: `/v2/demo/permit/business${cityQs}` },
        { label: 'Residential', to: `/v2/demo/permit/residential${cityQs}` },
        { label: 'Events', to: `/v2/demo/permit/events${cityQs}` },
        { label: 'Zoning check', to: `/zoning-check${cityQs}` },
        { label: 'Thin intake', to: `/v2/demo/permit/intake${cityQs}` },
        { label: 'Staff review', to: `/v2/demo/permit/staff${cityQs}` },
      ],
    },
    {
      id: 'more',
      label: 'More',
      items: [
        { label: 'Pitch deck', to: '/pitch-decks/yc.html', external: true },
        { label: 'Citizen portal', href: `${PS_BASE}/lnp/public`, external: true },
        { label: 'Agency portal', href: `${PS_BASE}/`, external: true },
        { label: 'Studio', to: '/studio' },
      ],
    },
  ];
}

function pathMatches(pathname, item) {
  if (!item.to) return false;
  const base = item.to.split('?')[0];
  if (item.exact) return pathname === base;
  if (base === '/v2/demo/permit') {
    return pathname === base;
  }
  return pathname === base || pathname.startsWith(`${base}/`);
}

export default function DemoSiteNav({
  homeTo = '/',
  showBack = true,
  sx = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const cityQs = useMemo(() => cityQuery(searchParams), [searchParams]);
  const sections = useMemo(() => buildNavSections(cityQs), [cityQs]);

  const goBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(homeTo);
  };

  return (
    <Box
      data-testid="demo-site-nav"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        flexShrink: 0,
        ...sx,
      }}
    >
      {showBack && (
        <Tooltip title="Back">
          <IconButton
            size="small"
            onClick={goBack}
            aria-label="Back"
            data-testid="demo-nav-back"
            sx={{
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              width: 34,
              height: 34,
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Home">
        <IconButton
          size="small"
          component={RouterLink}
          to={homeTo}
          aria-label="Home"
          data-testid="demo-nav-home"
          sx={{
            color: 'var(--citizen-primary, #1976d2)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            width: 34,
            height: 34,
          }}
        >
          <HomeOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Menu">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label="Open navigation menu"
          data-testid="demo-nav-menu"
          sx={{
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            width: 34,
            height: 34,
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: { xs: 'min(100vw, 320px)', sm: 340 }, bgcolor: '#fafbfc' },
          'data-testid': 'demo-nav-drawer',
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
            InterviewFlow
          </Typography>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            aria-label="Close navigation menu"
            data-testid="demo-nav-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {sections.map((section, idx) => (
          <List
            key={section.id}
            dense
            subheader={(
              <ListSubheader
                component="div"
                sx={{
                  bgcolor: 'transparent',
                  fontWeight: 700,
                  lineHeight: '36px',
                  color: 'text.secondary',
                }}
              >
                {section.label}
              </ListSubheader>
            )}
          >
            {section.items.map((item) => {
              const selected = pathMatches(location.pathname, item);
              if (item.href || item.external) {
                const href = item.href || item.to;
                return (
                  <ListItemButton
                    key={item.label}
                    component="a"
                    href={href}
                    target={item.href ? '_blank' : undefined}
                    rel={item.href ? 'noopener noreferrer' : undefined}
                    onClick={() => setOpen(false)}
                    selected={selected}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                );
              }
              return (
                <ListItemButton
                  key={item.label}
                  component={RouterLink}
                  to={item.to}
                  selected={selected}
                  onClick={() => setOpen(false)}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}
            {idx < sections.length - 1 ? <Divider sx={{ my: 0.5 }} /> : null}
          </List>
        ))}
      </Drawer>
    </Box>
  );
}
